import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  fetchSchemes,
  fetchObjectURLByObjectId,
  fetchApplicationNumber,
  fetchDepartments,
  fetchTypeOfSchemes,
} from "../api/fetch-scheme";
import trackApplicationValidationSchema from "../validation/trackApplicationValidationSchema";
// import MobileOTP from "../modal/MobileOTP";

const CAPTCHA_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const createCaptchaCode = (length = 6) =>
  Array.from({ length }, () =>
    CAPTCHA_CHARS.charAt(Math.floor(Math.random() * CAPTCHA_CHARS.length))
  ).join("");

function TrackApplication() {
  const navigate = useNavigate();
  const [schemeTypes, setSchemeTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [isSchemeTypesLoading, setIsSchemeTypesLoading] = useState(false);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false);
  const [isSchemesLoading, setIsSchemesLoading] = useState(false);
  const [captchaCode, setCaptchaCode] = useState(() => createCaptchaCode());
  const [objectInfo, setObjectInfo] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [isSubmittingSearch, setIsSubmittingSearch] = useState(false);
  const [draftPopup, setDraftPopup] = useState({
    show: false,
    message: "",
  });
  // const [mobileNumberVerification, setMobileNumberVerification] = useState({
  //   show: false,
  //   verified: false,
  //   mobileNumber: "",
  //   application: null
  // });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(trackApplicationValidationSchema),
    defaultValues: {
      schemeType: "",
      department: "",
      schemeName: "",
      applicationNo: "",
      captchaValue: "",
      generatedCaptcha: captchaCode,
    },
  });

  useEffect(() => {
    const loadSchemeTypes = async () => {
      setIsSchemeTypesLoading(true);

      try {
        const data = await fetchTypeOfSchemes();
        setSchemeTypes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load track application scheme types:", error);
        setSchemeTypes([]);
      } finally {
        setIsSchemeTypesLoading(false);
      }
    };

    loadSchemeTypes();
  }, []);

  useEffect(() => {
    setValue("generatedCaptcha", captchaCode, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [captchaCode, setValue]);

  // useEffect(() => {
  //   if (
  //     !mobileNumberVerification.verified ||
  //     !mobileNumberVerification.application
  //   ) {
  //     return;
  //   }
  //
  //   navigate("/application-status", {
  //     state: {
  //       application: mobileNumberVerification.application,
  //       objectInfo: objectInfo,
  //       schemeconfiguratorId: getValues("schemeName")
  //     },
  //   });
  // }, [mobileNumberVerification, navigate]);

  const captchaImage = useMemo(() => {
    const characters = captchaCode
      .split("")
      .map((char, index) => {
        const x = 16 + index * 18;
        const y = 24 + (index % 2 === 0 ? 2 : -2);
        const rotation = index % 2 === 0 ? -8 : 8;

        return `<text x="${x}" y="${y}" transform="rotate(${rotation} ${x} ${y})">${char}</text>`;
      })
      .join("");

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="140" height="42" viewBox="0 0 140 42">
        <rect width="140" height="42" rx="6" fill="#f8fbff" stroke="#cfd9e6" />
        <path d="M8 31 C28 8, 46 37, 64 16 S98 10, 132 24" stroke="#9db8d7" stroke-width="1.4" fill="none" />
        <path d="M10 12 C24 28, 43 4, 70 23 S106 30, 130 12" stroke="#d5a64f" stroke-width="1.2" fill="none" />
        <g fill="#123c84" font-family="monospace" font-size="22" font-weight="700">
          ${characters}
        </g>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }, [captchaCode]);

  const refreshCaptcha = () => {
    setCaptchaCode(createCaptchaCode());
    setValue("captchaValue", "", {
      shouldValidate: false,
      shouldDirty: true,
    });
    clearErrors("captchaValue");
  };

  const handleSearchFailure = (message) => {
    setSearchError(message);
    refreshCaptcha();
  };

  const handleSchemeTypeChange = async (schemeType) => {
    clearErrors(["schemeType", "department", "schemeName"]);
    setSearchError("");
    setDepartments([]);
    setSchemes([]);
    setObjectInfo(null);
    setValue("department", "", {
      shouldValidate: false,
      shouldDirty: true,
    });
    setValue("schemeName", "", {
      shouldValidate: false,
      shouldDirty: true,
    });
    setValue("applicationNo", "", {
      shouldValidate: false,
      shouldDirty: true,
    });

    if (!schemeType) {
      return;
    }

    setIsDepartmentsLoading(true);

    try {
      const data = await fetchDepartments(schemeType?.split("_")[0]);
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load track application departments:", error);
      setDepartments([]);
    } finally {
      setIsDepartmentsLoading(false);
    }
  };

  const handleDepartmentChange = async (departmentId) => {
    clearErrors(["department", "schemeName"]);
    setSearchError("");
    setSchemes([]);
    setObjectInfo(null);
    setValue("schemeName", "", {
      shouldValidate: false,
      shouldDirty: true,
    });
    setValue("applicationNo", "", {
      shouldValidate: false,
      shouldDirty: true,
    });

    if (!departmentId) {
      return;
    }

    setIsSchemesLoading(true);

    try {
      const data = await fetchSchemes(departmentId, getValues("schemeType"));
      setSchemes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load track application schemes:", error);
      setSchemes([]);
    } finally {
      setIsSchemesLoading(false);
    }
  };

  const handleSchemeChange = async (value) => {
    clearErrors("schemeName");
    setSearchError("");
    setObjectInfo(null);
    setValue("applicationNo", "", {
      shouldValidate: false,
      shouldDirty: true,
    });

    if (!value) {
      setObjectInfo(null);
      return;
    }

    try {
      const response = await fetchObjectURLByObjectId(value);
      // console.log(response);
      setObjectInfo(response || null);
    } catch (error) {
      console.error("Failed to fetch object URL:", error);
    }
  };

  const getApplicationDisplayValue = (application) =>
    application?.applicationreferencenumber ||
    "";

  const getApplicationMobileNumber = (application) =>
    application?.mobileNumber ||
    application?.mobilenumber ||
    application?.mobileNo ||
    application?.mobileno ||
    application?.phoneNumber ||
    application?.phonenumber ||
    application?.contactNumber ||
    application?.contactnumber ||
    application?.mobile ||
    "";

  const isDraftApplication = (application) => {
    const statusCode = Number(application?.status?.code);
    const statusLabel = String(
      application?.status?.label || application?.status || ""
    )
      .trim()
      .toLowerCase();

    return statusCode === 2 || statusLabel === "draft";
  };

  const handleApplicationChange = (event) => {
    setValue("applicationNo", event.target.value, {
      shouldValidate: false,
      shouldDirty: true,
    });
    setSearchError("");
    clearErrors("applicationNo");
  };

  const handleCaptchaChange = (event) => {
    setValue("captchaValue", event.target.value.toUpperCase(), {
      shouldValidate: false,
      shouldDirty: true,
    });
    clearErrors("captchaValue");
  };

  const onSubmit = async(data) => {
    setSearchError("");
    setIsSubmittingSearch(true);

    try {
      if (!objectInfo?.restContextPath) {
        handleSearchFailure(
          Liferay.Language.get("please-select-a-valid-scheme")
        );
        return;
      }

      const results = await fetchApplicationNumber(
        objectInfo?.restContextPath,
        data.applicationNo
      );
      // console.log("results status::::",results)
      const matchedApplications = Array.isArray(results) ? results : [];
      const exactMatch =
        matchedApplications.find(
          (application) =>
            String(getApplicationDisplayValue(application)) ===
            String(data.applicationNo)
        ) || matchedApplications[0];

      if (!exactMatch) {
        handleSearchFailure(Liferay.Language.get("application-not-found"));
        return;
      }

      // console.log("exactMatch:::::::",exactMatch)
      if (isDraftApplication(exactMatch)) {
        const message = Liferay.Language.get(
          "this-application-is-in-draft-status-and-cannot-be-tracked"
        );

        handleSearchFailure(message);
        setDraftPopup({
          show: true,
          message,
        });
        return;
      }

      // OTP verification is intentionally commented out for now.
      // const mobileNumber = String(
      //   getApplicationMobileNumber(exactMatch) || ""
      // ).trim();
      //
      // if (!mobileNumber) {
      //   handleSearchFailure(
      //     Liferay.Language.get(
      //       "mobile-number-is-not-available-for-this-application"
      //     )
      //   );
      //   return;
      // }
      //
      // setMobileNumberVerification({
      //   show: true,
      //   verified: false,
      //   mobileNumber: mobileNumber,
      //   application: exactMatch
      // });

      navigate("/application-status", {
        state: {
          application: exactMatch,
          objectInfo: objectInfo,
          schemeconfiguratorId: data.schemeName,
        },
      });
    } catch (error) {
      console.error("Error while searching application:", error);
      handleSearchFailure(
        Liferay.Language.get(
          "unable-to-search-application-please-try-again"
        )
      );
    } finally {
      setIsSubmittingSearch(false);
    }
  };

  return (
    <div className="container py-3 py-lg-4">
      <div className="terms-card-wrap">
        <div className="terms-card">
          <div className="terms-card-head">{Liferay.Language.get("track-application")}</div>

          <div className="terms-card-body">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row g-3 track-form-row">
                <div className="col-lg-4 col-md-6">
                  <div className="form-group">
                    <label
                      className="track-form-label"
                      htmlFor="track-scheme-type"
                    >
                      {Liferay.Language.get("scheme-type")}<span className="text-danger">*</span>
                    </label>
                    <select
                      id="track-scheme-type"
                      className={`form-control track-form-control track-select ${
                        errors.schemeType ? "track-form-control-error" : ""
                      }`}
                      disabled={isSchemeTypesLoading}
                      {...register("schemeType", {
                        onChange: (event) =>
                          handleSchemeTypeChange(event.target.value),
                      })}
                    >
                      <option value="">
                        {isSchemeTypesLoading
                          ? Liferay.Language.get("loading-scheme-types")
                          : Liferay.Language.get("select-scheme-type")}
                      </option>
                      {schemeTypes.map((schemeType) => {
                        const schemeTypeValue = String(schemeType?.code || "");

                        return (
                          <option key={schemeType?.id || schemeTypeValue} value={schemeTypeValue+"_"+schemeType?.schemeType}>
                            {schemeType?.schemeType || schemeTypeValue}
                          </option>
                        );
                      })}
                    </select>
                    {errors.schemeType ? (
                      <div className="track-field-error">
                        {errors.schemeType.message}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  <div className="form-group">
                    <label
                      className="track-form-label"
                      htmlFor="track-department"
                    >
                     {Liferay.Language.get("department")}<span className="text-danger">*</span>
                    </label>
                    <select
                      id="track-department"
                      className={`form-control track-form-control track-select ${
                        errors.department ? "track-form-control-error" : ""
                      }`}
                      disabled={!departments.length || isDepartmentsLoading}
                      {...register("department", {
                        onChange: (event) =>
                          handleDepartmentChange(event.target.value),
                      })}
                    >
                      <option value="">
                        {isDepartmentsLoading
                          ? Liferay.Language.get("loading-departments")
                          : Liferay.Language.get("select-department")}
                      </option>
                      {departments.map((department) => {
                        const departmentValue = String(
                          department?.id || department?.departmentId || ""
                        );

                        return (
                          <option key={departmentValue} value={departmentValue}>
                            {department?.name ||
                              department?.departmentName ||
                              departmentValue}
                          </option>
                        );
                      })}
                    </select>
                    {errors.department ? (
                      <div className="track-field-error">
                        {errors.department.message}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  <div className="form-group">
                    <label className="track-form-label" htmlFor="track-scheme">
                     {Liferay.Language.get("scheme-name")} <span className="text-danger">*</span>
                    </label>
                    <select
                      id="track-scheme"
                      className={`form-control track-form-control track-select ${
                        errors.schemeName ? "track-form-control-error" : ""
                      }`}
                      disabled={!schemes.length || isSchemesLoading}
                      {...register("schemeName", {
                        onChange: (event) => handleSchemeChange(event.target.value),
                      })}
                    >
                      <option value="">
                        {isSchemesLoading
                          ? Liferay.Language.get("loading-schemes")
                          : Liferay.Language.get("select-scheme")}
                      </option>
                      {schemes &&
                        schemes.map((scheme) => (
                          <option
                            key={scheme.id}
                            value={scheme.id + "_" + scheme.targetObjectDefinitionId}
                          >
                            {scheme.schemeName}
                          </option>
                        ))}
                    </select>
                    {errors.schemeName ? (
                      <div className="track-field-error">
                        {errors.schemeName.message}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="row align-items-end g-3 mt-4">
                <div className="col-lg-4 col-md-6">
                  <div className="form-group">
                    <label className="track-form-label" htmlFor="application-no">
                      {Liferay.Language.get("application-id")} <span className="text-danger">*</span>
                    </label>
                    <input
                      id="application-no"
                      type="text"
                      className={`form-control track-form-control ${
                        errors.applicationNo ? "track-form-control-error" : ""
                      }`}
                      placeholder={Liferay.Language.get("enter-your-application-id")}
                      {...register("applicationNo", {
                        onChange: handleApplicationChange,
                      })}
                    />
                    {errors.applicationNo ? (
                      <div className="track-field-error">
                        {errors.applicationNo.message}
                      </div>
                    ) : null}
                    {searchError ? (
                      <div className="track-field-error">{searchError}</div>
                    ) : null}
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  <div className="form-group">
                    <div className="track-captcha-label-row">
                      <label
                        className="track-form-label mb-0"
                        htmlFor="captcha-input"
                      >
                        {Liferay.Language.get("captcha")} <span className="text-danger">*</span>
                      </label>
                      <button
                        type="button"
                        className="track-captcha-refresh-button"
                        onClick={refreshCaptcha}
                        aria-label={Liferay.Language.get("refresh-captcha")}
                        title={Liferay.Language.get("refresh-captcha")}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M20 11A8 8 0 0 0 6.34 5.34L4 7.67M4 7.67V4m0 3.67h3.67M4 13a8 8 0 0 0 13.66 5.66L20 16.33M20 16.33V20m0-3.67h-3.67"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="track-captcha-row">
                      <img
                        src={captchaImage}
                        alt={Liferay.Language.get("captcha-image")}
                        className="track-captcha-image"
                        onClick={refreshCaptcha}
                        title={Liferay.Language.get("click-to-refresh-captcha")}
                      />
                      <input
                        id="captcha-input"
                        type="text"
                        className={`form-control track-form-control track-captcha-input ${
                          errors.captchaValue ? "track-form-control-error" : ""
                        }`}
                        placeholder={Liferay.Language.get("enter-captcha")}
                        autoComplete="off"
                        {...register("captchaValue")}
                        onChange={handleCaptchaChange}
                      />
                    </div>
                    {errors.captchaValue ? (
                      <div className="track-field-error">
                        {errors.captchaValue.message}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-lg-4 col-md-6">
                  <div className="track-search-action">
                    <button
                      type="submit"
                      className="btn track-search-button"
                      disabled={isSubmittingSearch}
                    >
                      {isSubmittingSearch
                        ? Liferay.Language.get("searching")
                        : Liferay.Language.get("search")}
                    </button>
                  </div>
                </div>
              </div>
            </form>
            {/* OTP verification is kept here as commented code for future re-enable.
            {mobileNumberVerification.show ? (
              <MobileOTP
                data={{
                  mobileNumber: mobileNumberVerification.mobileNumber,
                  application: mobileNumberVerification.application
                }}
                setMobileNumberVerification={setMobileNumberVerification}
              />
            ) : null}
            */}
            {draftPopup.show ? (
              <>
                <div className="modal-backdrop fade show"></div>
                <div
                  className="modal fade show d-block"
                  tabIndex="-1"
                  aria-modal="true"
                  role="dialog"
                >
                  <div
                    className="modal-dialog modal-dialog-centered"
                    style={{ maxWidth: "420px", width: "100%" }}
                  >
                    <div className="modal-content shadow-lg border-0 rounded-2 overflow-hidden">
                      <div
                        className="modal-header text-white py-2 px-3"
                        style={{
                          background:
                            "linear-gradient(135deg,#12397f,#2563ff)",
                        }}
                      >
                        <h5 className="modal-title fw-bold mb-0 fs-6">
                          {Liferay.Language.get("application-status")}
                        </h5>
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          onClick={() =>
                            setDraftPopup({ show: false, message: "" })
                          }
                          aria-label={Liferay.Language.get("close")}
                          style={{ transform: "scale(0.8)" }}
                        />
                      </div>

                      <div
                        className="modal-body p-3"
                        style={{ background: "#f8f9fc" }}
                      >
                        <div className="text-center">
                          <div className="mb-2">
                            <div
                              className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                              style={{
                                width: "52px",
                                height: "52px",
                                background: "#12397f",
                                fontSize: "1.35rem",
                              }}
                            >
                              !
                            </div>
                          </div>
                          <p
                            className="mb-0 text-dark"
                            style={{ fontSize: "0.98rem" }}
                          >
                            {draftPopup.message}
                          </p>
                        </div>
                      </div>

                      <div className="modal-footer border-0 p-2 bg-light justify-content-end">
                        <button
                          type="button"
                          className="btn btn-primary rounded-1 px-4 py-1"
                          onClick={() =>
                            setDraftPopup({ show: false, message: "" })
                          }
                          style={{ fontSize: "0.9rem" }}
                        >
                          {Liferay.Language.get("ok")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrackApplication;
